import type { FunctionComponent, ReactNode } from 'react';
import { useCallback } from 'react';
import classNames from 'classnames';
import { Button, List } from '@george-labs.com/design-system';

import { useIsOpen } from '@luna/utils-ui';

import { DevToolsDockButton } from './DevToolsDockButton';
import styles from './DevToolsDock.module.scss';

interface Props {
    children: ReactNode;
    isRecording: boolean;
    onDockCollapse?: () => void;
}

const DEV_TOOLS_DOCK_OPEN_STATE = 'devToolsOpen';

export const DevToolsDockPanel: FunctionComponent<Props> = ({
    children,
    isRecording,
    onDockCollapse,
}) => {
    const [isOpen, expandDock, collapseDock] = useIsOpen(
        JSON.parse(localStorage.getItem(DEV_TOOLS_DOCK_OPEN_STATE) ?? JSON.stringify(true)),
    );
    const collapseDockAndCloseDropdowns = useCallback(() => {
        collapseDock();
        onDockCollapse?.();
    }, [collapseDock, onDockCollapse]);

    const toggleDock = useCallback(() => {
        if (isOpen) {
            collapseDockAndCloseDropdowns();
            localStorage.setItem(DEV_TOOLS_DOCK_OPEN_STATE, JSON.stringify(false));
        } else {
            expandDock();
            localStorage.setItem(DEV_TOOLS_DOCK_OPEN_STATE, JSON.stringify(true));
        }
    }, [isOpen, expandDock, collapseDockAndCloseDropdowns]);

    const toggleIconClassName = classNames(styles.toggleIcon, {
        [styles.closedIcon]: !isOpen,
    });

    const toolListClassName = classNames('position-relative', styles.toolList, {
        [styles.closed]: !isOpen,
    });

    return (
        <aside className={`d-flex d-print-none align-items-center ${styles.dock}`}>
            <DevToolsDockButton
                size={Button.SIZE.SMALL}
                icon={Button.ICON.ARROW_FAST_FORWARD}
                iconClassName={toggleIconClassName}
                showRecordingIndicator={isRecording}
                indicatorClassName={`position-absolute ${styles.recordingIndicator}`}
                indicatorLabel="Request recording in progress"
                onClick={toggleDock}
            >
                Dev tools toggle
            </DevToolsDockButton>
            <List variant={List.VARIANT.UNSTYLED} className={toolListClassName}>
                {children}
            </List>
        </aside>
    );
};
